import { isCalendarDate } from '@web/shared/util/zoned-time';
import {
  SUBJECT_TYPES,
  TASK_PRIORITIES,
  type TaskBucket,
  type TaskCountsQuery,
  type TaskPriority,
  type SubjectType,
} from '../contract';
import { TASK_GROUPS, type TaskGroup } from './kanban-model';

/**
 * The state of `/tasks` in the URL (spec 006 §7.2), in English:
 * `?tab=overdue|upcoming|done&scope=team&display=calendar|kanban&calendar=week|day&date=YYYY-MM-DD&groupBy=assignee|type|priority&completed=true&type=&priority=&subject=customer|deal|none&assignee=&task=<id>`.
 * The defaults stay out of the URL: the "today" tab, "mine", the list, the month and the
 * board grouped by due day without the completed tasks. The board has no tab (its columns
 * are the tabs); `groupBy` and `completed` belong to the board only.
 */
export const TASK_TABS = [
  'today',
  'overdue',
  'upcoming',
  'done',
] as const satisfies readonly TaskBucket[];
export const CALENDAR_VIEWS = ['month', 'week', 'day'] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];

export interface TasksSearch {
  tab?: Exclude<TaskBucket, 'today'>;
  scope?: 'team';
  display?: 'calendar' | 'kanban';
  /** How the board groups its columns (the due day by default). */
  groupBy?: Exclude<TaskGroup, 'due'>;
  /** The board shows the completed tasks in a last column. */
  completed?: true;
  calendar?: Exclude<CalendarView, 'month'>;
  /** The reference day of the calendar. */
  date?: string;
  type?: string;
  priority?: TaskPriority;
  subject?: SubjectType | 'none';
  assignee?: string;
  /** The task open in the sheet. */
  task?: string;
}

const isOneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (list as readonly string[]).includes(value);

const text = (value: unknown) =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined;

/** Reads the state from the URL; anything malformed is dropped (never an error page). */
export function parseTasksSearch(raw: Record<string, unknown>): TasksSearch {
  const search: TasksSearch = {};
  const tab = raw['tab'];
  if (tab === 'overdue' || tab === 'upcoming' || tab === 'done') search.tab = tab;
  if (raw['scope'] === 'team') search.scope = 'team';
  const display = raw['display'];
  if (display === 'calendar' || display === 'kanban') search.display = display;
  if (display === 'kanban') {
    const groupBy = raw['groupBy'];
    if (isOneOf(TASK_GROUPS, groupBy) && groupBy !== 'due') search.groupBy = groupBy;
    if (raw['completed'] === true || raw['completed'] === 'true') search.completed = true;
    // The board's columns replace the tabs.
    delete search.tab;
  }
  const calendar = raw['calendar'];
  if (calendar === 'week' || calendar === 'day') search.calendar = calendar;
  const date = text(raw['date']);
  if (date && isCalendarDate(date)) search.date = date;
  for (const key of ['type', 'assignee', 'task'] as const) {
    const value = text(raw[key]);
    if (value) search[key] = value;
  }
  if (isOneOf(TASK_PRIORITIES, raw['priority'])) search.priority = raw['priority'];
  const subject = raw['subject'];
  if (subject === 'none' || isOneOf(SUBJECT_TYPES, subject)) search.subject = subject;
  return search;
}

/** The filters as the API reads them (the tab and the period aside). */
export function taskFilters(search: TasksSearch, team: boolean): TaskCountsQuery {
  return {
    ...(team && search.scope === 'team' ? { scope: 'team' as const } : {}),
    ...(search.type ? { typeId: search.type } : {}),
    ...(search.priority ? { priority: search.priority } : {}),
    ...(search.subject ? { subjectType: search.subject } : {}),
    ...(team && search.scope === 'team' && search.assignee
      ? { assigneeMembershipId: search.assignee }
      : {}),
  };
}

/**
 * A filter of the "Filtros" panel, one chip each when the panel is closed: type, link,
 * priority, assignee (only in "Equipe") and, on the board, "Mostrar concluídas".
 */
export type TaskPanelFilter = 'type' | 'subject' | 'priority' | 'assignee' | 'completed';

/**
 * The panel's filters that are on, in the panel's order. `canSeeTeam`: the person's data
 * scope reaches beyond their own (else "Equipe", and so the assignee, means nothing).
 */
export function activeTaskFilters(search: TasksSearch, canSeeTeam = true): TaskPanelFilter[] {
  const team = canSeeTeam && search.scope === 'team';
  const kanban = search.display === 'kanban';
  return (
    [
      ['type', Boolean(search.type)],
      ['subject', Boolean(search.subject)],
      ['priority', Boolean(search.priority)],
      ['assignee', team && Boolean(search.assignee)],
      ['completed', kanban && search.completed === true],
    ] as const
  )
    .filter(([, on]) => on)
    .map(([key]) => key);
}

export function hasTaskFilters(search: TasksSearch, canSeeTeam = true): boolean {
  return activeTaskFilters(search, canSeeTeam).length > 0;
}

/** The search without one of the panel's filters (its chip's ×). */
export function withoutTaskFilter(search: TasksSearch, key: TaskPanelFilter): TasksSearch {
  const next: TasksSearch = { ...search };
  delete next[key];
  return next;
}

/** The search without the panel's filters (the view, the scope, the tab and the period stay). */
export function withoutTaskFilters(search: TasksSearch): TasksSearch {
  const next: TasksSearch = { ...search };
  delete next.type;
  delete next.priority;
  delete next.subject;
  delete next.assignee;
  delete next.completed;
  return next;
}

/** Changes the search; empty values leave the URL. */
export function withTaskSearch(search: TasksSearch, changes: Partial<TasksSearch>): TasksSearch {
  const next: TasksSearch = { ...search, ...changes };
  for (const [key, value] of Object.entries(next)) {
    if (value === undefined || value === '') delete next[key as keyof TasksSearch];
  }
  return next;
}

/**
 * Switches between the list, the calendar and the board: the board drops the tab (its
 * columns are the tabs); the list and the calendar drop the board's grouping and toggle.
 */
export function withTaskDisplay(
  search: TasksSearch,
  display: 'list' | 'calendar' | 'kanban',
): TasksSearch {
  const next = withTaskSearch(search, { display: display === 'list' ? undefined : display });
  if (display === 'kanban') {
    delete next.tab;
  } else {
    delete next.groupBy;
    delete next.completed;
  }
  return next;
}
